import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../config/axiosConfig";
import { useParams } from "react-router-dom";
import Parc from "../../components/Parcours/Parc";
import EleveDescr from "../../components/Eleves/EleveDescr";
import EleveGroupe from "../../components/Eleves/EleveGroupe";
import ElevePdf from "../../components/Eleves/ElevePdf";
import { PDFDownloadLink } from "@react-pdf/renderer";
import "../../style/Eleves/Eleves.css";
import EleveTuteur from "../../components/Eleves/EleveTuteur";
import ConventionActions from "../../components/Eleves/ConventionActions";
import { extractApiErrorMessage } from "../../utils/apiErrorMessage";

function Eleve(props) {
  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const personne = localStorage.getItem("personne");

  const nbEleveMax = props.nbEleveMax;
  const semaine = props.semaine;

  let { id } = useParams();
  const isOwnStudentView = personne === "eleves" && String(userId) === String(id);

  const [eleve, setEleve] = useState(null);
  const [actionMessage, setActionMessage] = useState("");
  const [actionError, setActionError] = useState("");

  const loadEleve = useCallback(() => {
    axiosInstance
      .get(`/eleves/${id}`)
      .then((res) => {
        setEleve(res.data);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [id]);

  useEffect(() => {
    loadEleve();
  }, [loadEleve]);

  const handleValide = (id) => {
    const confirm = window.confirm(
      "Êtes-vous sûr de vouloir valider cet élève ?"
    );

    if (confirm) {
      setActionMessage("");
      setActionError("");
      axiosInstance
        .put(`eleves/confirmation/${id}`)
        .then(() => {
          setActionMessage("Le tuteur de cet eleve a bien ete attribue.");
          loadEleve();
        })
        .catch((err) => {
          console.error(err);
          setActionError(
            extractApiErrorMessage(
              err,
              "Le tuteur n'a pas pu etre attribue a cet eleve."
            )
          );
        });
    }
  };

  const handleSupprime = (id) => {
    const confirmation = window.confirm(
      "Êtes-vous sûr de vouloir supprimer cet élève ?"
    );

    if (confirmation) {
      setActionMessage("");
      setActionError("");
      axiosInstance
        .delete(`eleves/${id}`)
        .then(() => {
          window.location.reload();
        })
        .catch((err) => {
          console.error(err);
          setActionError(
            extractApiErrorMessage(
              err,
              "La suppression de cet eleve n'a pas pu etre finalisee."
            )
          );
        });
    }
  };

  const handleParcours = (id) => {
    const confirmation = window.confirm(
      "Êtes-vous sûr de vouloir attribuer un parcours à cet élève ?"
    );

    if (confirmation) {
      setActionMessage("");
      setActionError("");
      const data = {
        nbEleveMax: parseInt(nbEleveMax),
        weekStart: semaine,
      };

      axiosInstance
        .put(`eleves/parcours/${id}`, data)
        .then(() => {
          setActionMessage("Le parcours de cet eleve a bien ete attribue.");
          loadEleve();
        })
        .catch((err) => {
          console.error(err);
          setActionError(
            extractApiErrorMessage(
              err,
              "Le parcours n'a pas pu etre attribue a cet eleve."
            )
          );
        });
    }
  };

  const hasTuteurRole =
    userRole === "Tuteur" || userRole === "Encadrant et Tuteur";
  const isAssignedTutor = Number.parseInt(userId, 10) === eleve?.professeurId;
  const canManageConvention =
    userRole === "Admin" || (hasTuteurRole && isAssignedTutor);
  const canDownloadConvention =
    Boolean(eleve?.conventionFinalePath) &&
    (canManageConvention || isOwnStudentView);
  const canAssignParcours =
    userRole === "Admin" &&
    !eleve?.parcoursId &&
    Boolean(eleve?.professeurId) &&
    Boolean(eleve?.conventionFinalePath);
  const isAdministrativeReady =
    Boolean(eleve?.professeurId) && Boolean(eleve?.conventionFinalePath);

  return (
    eleve && (
      <div className="global-eleve">
        <div className="contain-eleve">
          <div className="contain-eleve-descr">
            <EleveDescr id={id} />
            {userRole === "Admin" && (
              <button className="btn" onClick={() => handleSupprime(eleve.id)}>
                Supprimer
              </button>
            )}
            {((personne === "eleves" && userId === id) ||
              userRole === "Admin" ||
              parseInt(userId) === eleve.professeurId) && (
              <PDFDownloadLink
                className="link pdf"
                document={<ElevePdf id={eleve.id} />}
                fileName={"eleve" + eleve.id + ".pdf"}
              >
                {({ loading }) =>
                  loading ? (
                    "Téléchargement en cours..."
                  ) : (
                    <>
                      <i className="fa-solid fa-circle-down fa-xl"></i>{" "}
                      Télécharger la fiche élève
                    </>
                  )
                }
              </PDFDownloadLink>
            )}

            <ConventionActions
              eleve={eleve}
              canUpload={canManageConvention}
              canDownload={canDownloadConvention}
              canDelete={canManageConvention}
              onConventionUpdated={setEleve}
            />

            {actionMessage && (
              <p className="eleve-feedback is-success">{actionMessage}</p>
            )}

            {actionError && (
              <p className="eleve-feedback is-error">{actionError}</p>
            )}
          </div>

          {userRole === "Admin" && (
            <div className="contain-parcours">
              <EleveTuteur eleve={eleve} onEleveUpdated={setEleve} />
              {eleve.parcoursId && !isAdministrativeReady && (
                <p className="eleve-feedback is-error">
                  Cet eleve a deja un parcours, mais son dossier administratif
                  n'est plus complet. Verifie le tuteur et la convention avant
                  validation finale.
                </p>
              )}
            </div>
          )}

          {!isOwnStudentView &&
            ((personne === "eleves" && userId === id) ||
            userRole === "Admin" ||
            parseInt(userId) === eleve.professeurId) &&
            eleve.parcoursId && (
              <div className="groupe-questions">
                <div>
                  <EleveGroupe id={id} eleve={eleve} />
                </div>
              </div>
            )}

          {userRole === "Admin" && (
            <div className="all-btn">
              {!eleve.professeurId && (
                <button className="btn" onClick={() => handleValide(eleve.id)}>
                  Valider
                </button>
              )}

              {canAssignParcours && (
                <button
                  className="btn"
                  onClick={() => handleParcours(eleve.id)}
                >
                  Attribuer un parcours
                </button>
              )}
            </div>
          )}
        </div>
        {!isOwnStudentView && eleve.parcoursId && (
          <div className="contain-parcours">
            <h1>Mon parcours :</h1>
            <Parc parcoursId={eleve.parcoursId} eleve={eleve} semaine={semaine} />
          </div>
        )}
      </div>
    )
  );
}

export default Eleve;
